import { Component, inject, OnInit } from '@angular/core';
import { LocationService } from './location.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { INDIAN_STATES } from '../../core/constants/indian-states';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';

@Component({
    selector: 'app-locations',
    standalone: true,
    imports: [FormsModule, RouterLink, CommonModule, NgSelectModule],
    templateUrl: './locations.component.html',
    styleUrl: './locations.component.scss',
})
export class LocationsComponent implements OnInit {
    locations: any[] = [];
    locationToDelete: any;
    currentPage = 1;
    pageSize = 10;
    totalLocations = 0;
    pageCount = 1;
    locationsLoading = false;
    locationInput$ = new Subject<string>();
    searchTerm = '';
    protected readonly Math = Math;

    private locationService = inject(LocationService);
    private modalService = inject(NgbModal);
    private toastr = inject(ToastrService);
    private authService = inject(AuthService);
    private confirmDialog = inject(ConfirmDialogService);

    /** Every location (unfiltered, unpaged) — feeds the Add store picker. */
    private allLocations: any[] = [];

    // ── Stores list ──
    stores: any[] = [];
    storesLoading = false;
    storeSearch = '';
    storeStatusBusy = new Set<string>();

    /**
     * Store dialog. 'create' tags a store to a location (from a location row,
     * or picked inside the dialog via Add store); 'edit' changes an existing
     * store's details — code, short code and location are fixed once created.
     */
    storeDialogMode: 'create' | 'edit' | null = null;
    /** Location being tagged with a new store / warehouse record. */
    tagLocation: any = null;
    /** Pick the location inside the dialog (opened from Add store). */
    pickLocation = false;
    editingStore: any = null;
    storeForm: any = {};
    storeSaving = false;
    storeError = '';
    readonly indianStates = INDIAN_STATES;

    ngOnInit(): void {
        this.getLocations();
        this.getStores();
        this.locationInput$
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(() => {
                this.currentPage = 1;
                this.getLocations();
            });
    }

    getLocations(): void {
        this.locationsLoading = true;
        this.locationService.getLocations(undefined, true).subscribe({
            next: (res: any) => {
                // Sites first, each followed by the locations under it, so a
                // sub-location (or a store mistakenly given a parent) stays visible.
                this.allLocations = res?.data || res || [];
                this.locations = this.orderByParent(this.allLocations);

                // Frontend filtering since API doesn't seem to support it yet based on the GET /locations specs
                if (this.searchTerm.trim()) {
                    this.locations = this.locations.filter(loc =>
                        loc.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                        loc.type.toLowerCase().includes(this.searchTerm.toLowerCase())
                    );
                }

                this.totalLocations = this.locations.length;
                this.pageCount = Math.ceil(this.totalLocations / this.pageSize) || 1;

                // Manual pagination
                const start = (this.currentPage - 1) * this.pageSize;
                const end = start + this.pageSize;
                this.locations = this.locations.slice(start, end);

                this.locationsLoading = false;
            },
            error: (error: any) => {
                console.error('Error fetching locations:', error);
                this.toastr.error('Failed to fetch locations.');
                this.locationsLoading = false;
            },
        });
    }

    private orderByParent(all: any[]): any[] {
        const byId = new Map(all.map((l) => [l.id, l]));
        const children = new Map<string, any[]>();
        const roots: any[] = [];
        for (const l of all) {
            if (l.parentLocationId && byId.has(l.parentLocationId)) {
                const list = children.get(l.parentLocationId) ?? [];
                list.push(l);
                children.set(l.parentLocationId, list);
            } else {
                roots.push(l);
            }
        }
        const out: any[] = [];
        const walk = (l: any, depth: number) => {
            const parent = byId.get(l.parentLocationId);
            out.push({
                ...l,
                depth,
                parentName: parent?.name ?? null,
                // A Store / Warehouse is a site of its own and never belongs under another.
                misParented: !!parent && (l.type === 'Store' || l.type === 'Warehouse'),
            });
            for (const c of children.get(l.id) ?? []) walk(c, depth + 1);
        };
        roots.forEach((r) => walk(r, 0));
        return out;
    }

    pageSizeChange() {
        this.currentPage = 1;
        this.getLocations();
    }

    onSearch() {
        this.currentPage = 1;
        this.locationInput$.next(this.searchTerm.trim());
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.pageCount || page === this.currentPage) return;
        this.currentPage = page;
        this.getLocations();
    }

    getVisiblePages(): number[] {
        const pages: number[] = [];
        const start = Math.max(2, this.currentPage - 2);
        const end = Math.min(this.pageCount - 1, this.currentPage + 2);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    }

    toggleStatus(location: any): void {
        const newStatus = !location.isActive;
        this.locationsLoading = true;

        this.locationService.updateLocation(location.id, { isActive: newStatus }).subscribe({
            next: () => {
                location.isActive = newStatus;
                this.toastr.success('Location status updated successfully!');
                this.locationsLoading = false;
            },
            error: (error: any) => {
                console.error('Error updating location status:', error);
                this.toastr.error('Failed to update location status.');
                this.locationsLoading = false;
            },
        });
    }

    openDeleteConfirmationModal(content: any, location: any) {
        this.locationToDelete = location;
        this.modalService.open(content, {
            ariaLabelledBy: 'confirmDeleteModalLabel',
        });
    }

    confirmDelete() {
        if (!this.locationToDelete?.id) return;

        this.locationService.deleteLocation(this.locationToDelete.id).subscribe({
            next: () => {
                this.toastr.success(`${this.locationToDelete.name} marked as inactive.`);
                this.getLocations();
                this.modalService.dismissAll();
            },
            error: (err) => {
                console.error('Error deleting location:', err);
                this.toastr.error('Failed to delete the location.');
            },
        });
    }

    closeModal() {
        this.modalService.dismissAll();
    }

    /** The store / warehouse record tagged to a location, if any. */
    linkedStore(location: any): any {
        return location?.stores?.[0] ?? null;
    }

    // ───────────────────────────────────────────────────────── stores ──

    getStores(): void {
        this.storesLoading = true;
        this.locationService.getStores().subscribe({
            next: (res: any) => {
                this.stores = res?.data || res || [];
                this.storesLoading = false;
            },
            error: () => {
                this.toastr.error('Failed to fetch stores.');
                this.storesLoading = false;
            },
        });
    }

    get filteredStores(): any[] {
        const t = this.storeSearch.trim().toLowerCase();
        if (!t) return this.stores;
        return this.stores.filter((s) =>
            [s.name, s.storeCode, s.shortCode, s.city, s.gstin, s.location?.name, s.location?.type]
                .some((v) => (v || '').toLowerCase().includes(t)),
        );
    }

    placeLine(store: any): string {
        return [store.city, store.state, store.pincode].filter(Boolean).join(', ');
    }

    /** Active Store / Warehouse locations without a store record yet. */
    get untaggedLocations(): any[] {
        return this.allLocations.filter(
            (l) =>
                l.isActive &&
                (l.type === 'Store' || l.type === 'Warehouse') &&
                !l.parentLocationId &&
                !l.stores?.length,
        );
    }

    async toggleStoreStatus(store: any): Promise<void> {
        const next = !store.isActive;
        if (!next) {
            const ok = await this.confirmDialog.confirm({
                title: `Deactivate ${store.name}?`,
                message:
                    'It disappears from the store switcher and from store pickers, so no one can bill or raise documents there until it is activated again. Its stock and history are kept.',
                confirmText: 'Deactivate',
                cancelText: 'Cancel',
                tone: 'danger',
                icon: 'bi-shop',
            });
            if (!ok) return;
        }
        this.storeStatusBusy.add(store.storeCode);
        this.locationService.updateStore(store.storeCode, { isActive: next }).subscribe({
            next: () => {
                this.storeStatusBusy.delete(store.storeCode);
                store.isActive = next;
                this.toastr.success(`${store.name} ${next ? 'activated' : 'deactivated'}.`);
                this.getLocations();
                this.authService.refreshUser().subscribe({ error: () => { } });
            },
            error: (err) => {
                this.storeStatusBusy.delete(store.storeCode);
                const msg = err?.error?.message;
                this.toastr.error(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to update the store.');
            },
        });
    }

    private suggestedCodes(name: string): { storeCode: string; shortCode: string } {
        const code = (name || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
        return { storeCode: code, shortCode: code.slice(0, 3) };
    }

    private blankStoreForm(name = ''): any {
        return {
            name,
            ...this.suggestedCodes(name),
            gstin: '',
            fssaiNumber: '',
            address: '',
            city: '',
            state: '',
            pincode: '',
            phone: '',
            email: '',
            locationId: null,
        };
    }

    openTagStore(location: any): void {
        this.storeDialogMode = 'create';
        this.pickLocation = false;
        this.tagLocation = location;
        this.editingStore = null;
        this.storeError = '';
        this.storeForm = { ...this.blankStoreForm(location.name), locationId: location.id };
    }

    openAddStore(): void {
        this.storeDialogMode = 'create';
        this.pickLocation = true;
        this.tagLocation = null;
        this.editingStore = null;
        this.storeError = '';
        this.storeForm = this.blankStoreForm();
    }

    /** Add store: picking the location fills in name / codes the user hasn't typed. */
    onPickLocation(locationId: string | null): void {
        const loc = this.allLocations.find((l) => l.id === locationId) ?? null;
        this.tagLocation = loc;
        if (!loc) return;
        const f = this.storeForm;
        if (!f.name?.trim()) f.name = loc.name;
        if (!f.storeCode?.trim()) Object.assign(f, this.suggestedCodes(loc.name));
    }

    openEditStore(store: any): void {
        this.storeDialogMode = 'edit';
        this.pickLocation = false;
        this.editingStore = store;
        this.tagLocation = store.location ?? null;
        this.storeError = '';
        this.storeForm = {
            name: store.name ?? '',
            storeCode: store.storeCode,
            shortCode: store.shortCode ?? '',
            gstin: store.gstin ?? '',
            fssaiNumber: store.fssaiNumber ?? '',
            address: store.address ?? '',
            city: store.city ?? '',
            state: store.state ?? null,
            pincode: store.pincode ?? '',
            phone: store.phone ?? '',
            email: store.email ?? '',
            locationId: store.locationId,
        };
    }

    closeTagStore(): void {
        if (this.storeSaving) return;
        this.storeDialogMode = null;
        this.tagLocation = null;
        this.editingStore = null;
    }

    get isWarehouseTag(): boolean {
        return this.tagLocation?.type === 'Warehouse';
    }

    saveTagStore(): void {
        const f = this.storeForm;
        const trim = (v: any) => (typeof v === 'string' ? v.trim() : v);
        const editing = this.storeDialogMode === 'edit';
        if (!editing && !f.locationId) {
            this.storeError = 'Pick the location this store belongs to.';
            return;
        }
        // GSTIN is required for a new store; older stores may not have one,
        // so an edit can still save other fields without it.
        if (!trim(f.name) || !trim(f.storeCode) || (!editing && !trim(f.gstin)) || !trim(f.address)) {
            this.storeError = editing ? 'Name and address are required.' : 'Name, code, GSTIN and address are required.';
            return;
        }
        if (!editing && this.isWarehouseTag && !trim(f.shortCode)) {
            this.storeError = 'Short code is required for a warehouse — it prefixes transfer box numbers.';
            return;
        }
        if (!editing && trim(f.shortCode) && !/^[A-Za-z0-9]{1,3}$/.test(trim(f.shortCode))) {
            this.storeError = 'Short code must be 1–3 letters or digits.';
            return;
        }
        if (trim(f.fssaiNumber) && !/^\d{14}$/.test(trim(f.fssaiNumber))) {
            this.storeError = 'FSSAI number must be exactly 14 digits.';
            return;
        }

        this.storeSaving = true;
        this.storeError = '';
        const done = (message: string) => {
            this.storeSaving = false;
            this.toastr.success(message);
            this.closeTagStore();
            this.getLocations();
            this.getStores();
            // Pull new / renamed stores into the top-bar switcher.
            this.authService.refreshUser().subscribe({ error: () => { } });
        };
        const failed = (err: any, fallback: string) => {
            this.storeSaving = false;
            const msg = err?.error?.message;
            this.storeError = Array.isArray(msg) ? msg.join(', ') : msg || fallback;
        };

        if (editing) {
            // Identity fields (code, short code, location) can't change; blank
            // optional fields are sent as null so the API clears them ('' fails
            // the FSSAI 14-digit check).
            const payload: any = { name: trim(f.name), address: trim(f.address) };
            if (trim(f.gstin)) payload.gstin = trim(f.gstin).toUpperCase();
            for (const k of ['fssaiNumber', 'city', 'state', 'pincode', 'phone', 'email']) {
                payload[k] = trim(f[k]) || null;
            }
            this.locationService.updateStore(this.editingStore.storeCode, payload).subscribe({
                next: () => done(`${payload.name} updated.`),
                error: (err) => failed(err, 'Failed to update the store.'),
            });
            return;
        }

        const payload: any = {
            name: trim(f.name),
            storeCode: trim(f.storeCode).toUpperCase(),
            gstin: trim(f.gstin).toUpperCase(),
            address: trim(f.address),
            locationId: f.locationId,
            isActive: true,
        };
        for (const k of ['shortCode', 'fssaiNumber', 'city', 'state', 'pincode', 'phone', 'email']) {
            if (trim(f[k])) payload[k] = trim(f[k]);
        }
        if (payload.shortCode) payload.shortCode = payload.shortCode.toUpperCase();

        const locationName = this.tagLocation?.name ?? 'the location';
        this.locationService.createStore(payload).subscribe({
            next: () => done(`${payload.name} tagged to ${locationName}.`),
            error: (err) => failed(err, 'Failed to create the store.'),
        });
    }
}
